import {
  BaseDriver,
  ComponentFactory,
  DriverFactory,
} from '@wix/wix-ui-test-utils/driver-factory';
import {
  ProgressBarDataHooks,
  ProgressBarDataKeys,
  ProgressBarAriaKeys,
  ProgressBarInnerAriaKeys,
} from './DataHooks';

export interface LinearProgressBarCoreDriver extends BaseDriver {
  /** Get the width of the foreground bar (the progress) */
  getWidth(): string;
  /** Returns boolean that indicates if the success icon exists */
  isSuccessIconDisplayed(): boolean;
  /** Returns boolean that indicates if the error icon exists */
  isErrorIconDisplayed(): boolean;
  /** Returns boolean that indicates if the progress percentages text exists */
  isPercentagesProgressDisplayed(): boolean;
  /** Returns boolean that indicates if the prefix indication exists */
  hasPrefixIndication(): boolean;
  /** Returns boolean that indicates if the custom suffix indication exists */
  hasSuffixIndication(): boolean;
  /** Get the suffix indication's text */
  getSuffixIndicationText(): string;
  /** Get the prefix indication aria-hidden value */
  hasPrefixAriaHidden(): boolean;
  /** Get the progress indication aria-hidden value */
  hasProgressIndicatorAriaHidden(): boolean;
  /** Returns aria-label prop */
  getAriaLabel(): string;
  /** Get the progress percentages value */
  getValue(): string;
  /** Get the progress numeric value */
  getNumericValue(): number;
  /** Returns true if has progress completed (value is 100) */
  isCompleted(): boolean;
  /** Returns true if has error */
  hasError(): boolean;
  /** Returns min value prop */
  getMinValue(): number;
  /** Returns max value prop */
  getMaxValue(): number;
  /** Returns aria-valuenow prop */
  getAriaValueNow(): number;
  /** Returns aria-valuemax prop */
  getAriaValueMax(): number;
  /** Returns aria-valuemin prop */
  getAriaValueMin(): number;
  /** Returns role html attribute */
  getRoleAttribute(): string;
  /** Returns aria-valuetext prop */
  getAriaValueText(): string;
  /** Clicks the component */
  click(): void;
}

// @ts-ignore
export const linearProgressBarDriverFactory: DriverFactory<
  LinearProgressBarCoreDriver
> = ({ element }: ComponentFactory) => {
  const getElement = (dataHook: string) =>
    element?.querySelector(`[data-hook="${dataHook}"]`);
  const getValue = () =>
    !element
      ? null
      : getElement(ProgressBarDataHooks.progressPercentage)?.querySelector(
          'span',
        )?.innerHTML;
  const getDataAttribute = (
    key: string,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- TODO(backlog 20260610T131344): type generically; cascades into nullable driver-interface returns
    parsingFunction?: Function,
  ) => {
    if (!element || !element.getAttribute(key)) {
      return null;
    }
    const value = element.getAttribute(key);
    return !!parsingFunction && parsingFunction instanceof Function
      ? parsingFunction(value)
      : value;
  };

  return {
    exists: () => !!element,
    getWidth: () => {
      const el = getElement(ProgressBarDataHooks.foreground) as HTMLElement;
      return el ? el.style.width : '0';
    },
    isSuccessIconDisplayed: () =>
      !!getElement(ProgressBarDataHooks.successIcon),
    isErrorIconDisplayed: () => !!getElement(ProgressBarDataHooks.errorIcon),
    isPercentagesProgressDisplayed: () =>
      !!getElement(ProgressBarDataHooks.progressPercentage),
    getValue: () => getValue(),
    isCompleted: () =>
      // @ts-ignore
      getValue() >= getDataAttribute(ProgressBarDataKeys.max),
    hasError: () => !!element?.querySelector("[data-hook='error-icon']"),
    getNumericValue: () => getDataAttribute(ProgressBarDataKeys.value, Number),
    getMinValue: () => getDataAttribute(ProgressBarDataKeys.min, Number),
    getMaxValue: () => getDataAttribute(ProgressBarDataKeys.max, Number),
    getAriaValueNow: () =>
      getDataAttribute(ProgressBarAriaKeys.valuenow, Number),
    getAriaValueMax: () =>
      getDataAttribute(ProgressBarAriaKeys.valuemax, Number),
    getAriaValueMin: () =>
      getDataAttribute(ProgressBarAriaKeys.valuemin, Number),
    getRoleAttribute: () => getDataAttribute('role'),
    getAriaValueText: () => getDataAttribute(ProgressBarAriaKeys.valuetext),
    hasPrefixIndication: () =>
      !!getElement(ProgressBarDataHooks.prefixIndicator),
    hasSuffixIndication: () =>
      !!getElement(ProgressBarDataHooks.suffixIndicator),
    getSuffixIndicationText: () =>
      getElement(ProgressBarDataHooks.suffixIndicator)?.textContent,
    hasPrefixAriaHidden: () =>
      getElement(ProgressBarDataHooks.prefixIndicator)?.hasAttribute(
        ProgressBarInnerAriaKeys.hidden,
      ),
    hasProgressIndicatorAriaHidden: () =>
      getElement(ProgressBarDataHooks.progressIndicator)?.hasAttribute(
        ProgressBarInnerAriaKeys.hidden,
      ),
    getAriaLabel: () =>
      getElement(ProgressBarDataHooks.container)?.getAttribute(
        ProgressBarInnerAriaKeys.label,
      ),
    click: () => {
      const el = getElement(ProgressBarDataHooks.container) as HTMLElement;
      el.click();
    },
  };
};
